// Authenticated fetch wrapper. Mirrors what the legacy auth.js does
// for vanilla pages, but as an explicit function instead of a global
// window.fetch monkey-patch — easier to reason about and to test.
//
// Headers injected:
//   Authorization: Bearer <jwt>           ← localStorage.token
//   X-Brand-Id:    <brandId>              ← localStorage.brand_id (if set)
//   X-Advertiser-Id: <advertiserId>       ← localStorage.advertiser_id (if set)
//
// Status handling:
//   401              → clear token + redirect to login
//   403 NO_ADVERTISER → redirect to /onboarding.html
//   403 ADVERTISER_FORBIDDEN → clear advertiser_id + reload
//   anything else    → return the response untouched
//
// Caller deals with non-2xx outcomes the same way the legacy code did.

const LOGIN_URL = '/login.html';
// React-Router route inside the new app — Netlify's SPA fallback
// serves the app shell for any unknown path, so a hard navigation
// to /onboarding loads the React app and routes to OnboardingPage.
const ONBOARDING_URL = '/onboarding';

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const brandId = localStorage.getItem('brand_id');
  if (brandId) headers['X-Brand-Id'] = brandId;
  const advertiserId = localStorage.getItem('advertiser_id');
  if (advertiserId) headers['X-Advertiser-Id'] = advertiserId;
  return headers;
}

export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  for (const [k, v] of Object.entries(authHeaders())) headers.set(k, v);
  const res = await fetch(input, { ...init, headers });
  await maybeHandleAuthFailure(res);
  return res;
}

// On 401 / specific 403 codes, mutate auth state + navigate. Returns
// nothing — the caller still receives the Response object so it can
// log/measure if it wants. The navigation always wins because we
// replace location after.
async function maybeHandleAuthFailure(res: Response): Promise<void> {
  if (res.status === 401) {
    clearAuth();
    window.location.replace(LOGIN_URL);
    return;
  }
  if (res.status === 403) {
    // We need to peek at the error code without consuming the body —
    // clone first so callers can still read the body.
    let code: string | null = null;
    try {
      const cloned = res.clone();
      const body = await cloned.json();
      code = body?.code || body?.error || null;
    } catch { /* not JSON, ignore */ }

    if (code === 'NO_ADVERTISER') {
      // Loop guard — onboarding-page calls (e.g. /api/me from the
      // page itself) would otherwise trigger another redirect to
      // the same URL. Skip if we're already there.
      if (!window.location.pathname.startsWith(ONBOARDING_URL)) {
        window.location.replace(ONBOARDING_URL);
      }
      return;
    }
    if (code === 'ADVERTISER_FORBIDDEN') {
      localStorage.removeItem('advertiser_id');
      window.location.reload();
      return;
    }
  }
}

export function clearAuth() {
  localStorage.removeItem('token');
  localStorage.removeItem('brand_id');
  localStorage.removeItem('brand_name');
  localStorage.removeItem('advertiser_id');
}

// Error thrown by apiJson on a non-2xx. Carries the STATUS and the PARSED BODY
// alongside the message.
//
// Why this exists: apiJson used to throw a bare Error with only the server's
// `error` string, so every structured field the backend sent — `code`, `reason`,
// and anything a caller needs to offer a recovery action — was parsed and then
// silently discarded. A caller that wants to branch on the kind of failure (not
// just show its text) had no way to. `.message` is unchanged, so every existing
// `err.message` call site behaves exactly as before.
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Narrow an unknown caught value to ApiError. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

// Convenience wrapper — calls apiFetch + asserts JSON. Throws on
// non-2xx with the server's error message when present.
export async function apiJson<T = unknown>(input: RequestInfo | URL, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(input, init);
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; }
  catch { /* keep null */ }
  if (!res.ok) {
    const msg = (data as { error?: string; message?: string } | null)?.error
             || (data as { error?: string; message?: string } | null)?.message
             || `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, data);
  }
  return data as T;
}
